import { motion } from "framer-motion";
import { BellRing, Gauge, KeyRound, ScrollText } from "lucide-react";

const SPEEDO_IMG =
  "https://images.unsplash.com/photo-1610913721979-b20ede600e63?crop=entropy&cs=srgb&fm=jpg&q=85";

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

const CARD = "rounded-2xl border border-line bg-panel p-6 md:p-8 hover:border-linehi transition-colors duration-300 relative overflow-hidden";

const RADAR_ROWS = [
  { plate: "BX22WDN", name: "Night Shift", tag: "MOT", days: 97, color: "#00E599" },
  { plate: "LD21FXG", name: "City Runabout", tag: "COUNCIL", days: 12, color: "#FFB800" },
  { plate: "SK23HRN", name: "Client Days", tag: "ROAD TAX", days: 64, color: "#00E599" },
];

export default function Bento() {
  return (
    <section id="watches" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div {...reveal()} className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">What it watches</p>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-wide mt-4 leading-[0.95]">
            One garage wall.<br />
            <span className="text-outline">Zero paperwork panic.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-14">
          {/* A — Expiration radar */}
          <motion.div {...reveal(0.05)} className={`${CARD} lg:col-span-8`} data-testid="bento-radar">
            <div className="absolute top-6 right-6 w-24 h-24 rounded-full border border-line overflow-hidden opacity-60">
              <div className="radar-disc w-full h-full rounded-full animate-radar-sweep" />
            </div>
            <div className="flex items-center gap-3">
              <Gauge className="w-5 h-5 text-ember" />
              <h3 className="font-display text-2xl font-bold uppercase tracking-wide">The expiration radar</h3>
            </div>
            <p className="text-mid text-sm mt-3 max-w-md">
              Every vehicle on one telemetry board — MOT, council test and road tax, each with a
              colour-coded countdown: mint when it's calm, amber when it's close, ember when it's urgent.
            </p>
            <div className="mt-7 space-y-2.5">
              {RADAR_ROWS.map((r) => (
                <div key={r.plate} className="flex items-center gap-4 rounded-xl border border-line bg-ink/60 px-4 py-3">
                  <span className="uk-plate rounded px-1.5 py-0.5 text-[11px] font-bold">{r.plate}</span>
                  <span className="font-mono text-xs text-mid truncate">{r.name}</span>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-low hidden sm:inline">{r.tag}</span>
                  <span className="font-mono text-xs font-bold rounded-full px-3 py-1" style={{ color: r.color, background: `${r.color}1a` }}>
                    {r.days}d
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* B — Email alerts (tall) */}
          <motion.div {...reveal(0.12)} className={`${CARD} lg:col-span-4 lg:row-span-2 flex flex-col`} data-testid="bento-emails">
            <div className="rounded-xl overflow-hidden border border-line">
              <img src={SPEEDO_IMG} alt="Dashboard gauges" className="w-full h-40 object-cover spotlight" />
            </div>
            <div className="flex items-center gap-3 mt-6">
              <BellRing className="w-5 h-5 text-amber2" />
              <h3 className="font-display text-2xl font-bold uppercase tracking-wide">Emails before deadlines bite</h3>
            </div>
            <p className="text-mid text-sm mt-3">
              Pick your alert checkpoints and a reminder inbox. One clean digest lands before
              anything expires — renewal logged, countdown reset.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {["30 days", "14 days", "7 days", "1 day", "Due day"].map((t, i) => (
                <span key={t} className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1.5 border ${i === 4 ? "border-ember text-ember" : "border-line text-mid"}`}>
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-auto pt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-low">
              Digest — one email, all deadlines
            </p>
          </motion.div>

          {/* C — Council tests */}
          <motion.div {...reveal(0.18)} className={`${CARD} lg:col-span-4`} data-testid="bento-council">
            <div className="flex items-center gap-3">
              <ScrollText className="w-5 h-5 text-volt" />
              <h3 className="font-display text-2xl font-bold uppercase tracking-wide">Council tests, badged</h3>
            </div>
            <p className="text-mid text-sm mt-3">
              Licensing and inspection dates for taxis and private-hire vehicles sit right
              next to the MOT — no separate spreadsheet, no nasty surprise at the rank.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {["Taxi", "Private hire", "Fleet van", "Executive"].map((t) => (
                <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] rounded-full border border-line text-mid px-3 py-1.5">
                  {t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* D — Passcode */}
          <motion.div {...reveal(0.24)} className={`${CARD} lg:col-span-4`} data-testid="bento-passcode">
            <div className="flex items-center gap-3">
              <KeyRound className="w-5 h-5 text-mint" />
              <h3 className="font-display text-2xl font-bold uppercase tracking-wide">Locked to your login</h3>
            </div>
            <p className="text-mid text-sm mt-3">
              Your fleet's data sits behind your own email and password — one private account
              for the whole garage, no shared codes to leak.
            </p>
            <div className="flex gap-2 mt-6">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className="w-10 h-10 rounded-lg border border-line bg-ink/60 flex items-center justify-center font-mono text-low">
                  •
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
